import { trendlessDeck } from "../decks"

const trendless = {
  slug: "trendless",
  title: "Trendless",
  category: "UI / UX",
  client: "Academic Team Project",
  duration: "7 Weeks",
  cover: "/assets/8X7OARkiaVXsQCtwTWOBiMfjFU.png",
  summary: "A seven-week team project at Algonquin: a campaign, a website and a browser plugin that flags sponsored content and slows impulse buying.",
  gallery: [
    "/assets/I0DVoBcpZHBzAw3QFwjcaCXWU.png",
    "/assets/kMCkEBGDbcPlDmv4sBatHalzt7g.png",
    "/assets/9oK2gzmRDc0yRssbRJKzMbtp9k.png",
  ],
  blocks: [
    {
      type: "deck",
      id: "trendless-deck",
      title: "Trendless 2.0",
      description: "From the 1.0 pitch to the product: a five-page website at desktop and mobile widths, three pop-up styles that explain why they fired, settings on every pop-up and a checkout pause, with focus-group quotes mapped to each decision.",
      deck: trendlessDeck,
    },
    {
      type: "richText",
      html: `<h4><strong>Trendless:</strong> A Human-Centred Design Project</h4>
<h5><strong>Overview</strong></h5>
<p>Trendless is a nonprofit awareness initiative and browser plugin designed to interrupt the cycle of social media micro-trends and hyper-consumerism. Built over seven weeks as part of a UI / UX design course at Algonquin College. </p>
<h5><strong>The Problem</strong></h5>
<p>Social media has evolved into a mass-scale engine for algorithmic spread, where aesthetics and products can explode in popularity almost overnight. The problem is systemic - accessible, low-cost items get pushed to enormous audiences, triggering participation not out of genuine desire but out of FOMO and the pursuit of belonging. Trends burn bright for weeks or months, then collapse, leaving consumers with products they no longer want and a compulsion to chase whatever comes next. The cycle produces real consequences: financial strain from continuous low-stakes purchases that accumulate, significant environmental waste from the rapid disposal of short-lifespan goods, and a gradual loss of personal identity as style and self-expression conform to whatever the algorithm is currently rewarding.</p>
<h5><strong>Research &amp; Discovery</strong></h5>
<p>We defined two primary personas. Athena, a 19-year-old navigating early adulthood and a recent BPD diagnosis, who relies on trend participation for a sense of community and belonging. And Zara, a full-time Vancouver content creator financially trapped in a cycle of buying and discarding products to maintain sponsor-worthy engagement numbers. Both users represent opposite ends of the same system, one consuming, one supplying, and both losing something in the process.</p>
<p>To pressure test our direction, we ran a focus group with five participants. The findings validated our core assumptions: most people struggle to identify undisclosed ads, nearly everyone expressed interest in a plugin that could flag sponsored content, and the consensus on checkout friction was positive, provided it came with customization options to avoid feeling punitive.</p>
<h5><strong>Design Principles</strong></h5>
<p>Three principles anchored every decision we made:</p>
<ul>
  <li>Raise awareness on the personal impact of micro-trends</li>
  <li>Design friction to disrupt the "buy now" dopamine loop</li>
  <li>Decouple social relevance from material consumption</li>
</ul>
<h5><strong>The Solution</strong></h5>
<p>Trendless operates as a three part system. A social media campaign uses the visual language of trending content to catch the target demographic mid-scroll; meeting them in the space where the problem lives. The campaign drives traffic to a website that serves as the initiative's hub: transparent about its mission, educational in its content, and trustworthy enough to convert visitors into plugin users.</p>
<p>The plugin itself is the core intervention. While a user scrolls, it actively surfaces undisclosed ads and sponsored content with a non-intrusive notification; making influence visible rather than invisible. At checkout, it introduces a brief, customizable micro-delay with a single prompt: <em>Does this fit your life, or just your cart?</em> The delay is short enough not to obstruct genuine purchases, but long enough to break the automated reflex of impulse buying. Both the pop-up format and the checkout timer are fully user-adjustable, a direct response to focus group concern about friction becoming annoyance.</p>
<p>The bold visual identity deliberately borrows from the aesthetic register of the platforms it's critiquing.</p>`,
    },
    {
      type: "image",
      src: "/assets/2c9FILNMDEQrioVMv3zJoSIaE.jpg",
      alt: "",
    },
  ],
  relatedSlugs: [
    "animatic-remastered",
    "seven-due-dates",
    "2024-chasing-greatness",
  ],
  seo: {
    title: "Trendless — Matia Dosen",
    description: "UX Designer and Creative",
  },
}

export default trendless
